/**
 * Hala Control Center - Sessions Management
 * Premium session monitoring - Connected to REAL client sessions
 */

import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { 
  Search, 
  Filter, 
  Eye,
  Flag,
  CheckCircle,
  Clock,
  AlertTriangle,
  MessageSquare,
  User,
  MapPin,
  Calendar,
  X,
  ChevronDown,
  Play,
  Pause,
  RefreshCw
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/control-center`;

// Auth helper
const getAuthHeaders = () => {
  const token = localStorage.getItem('hala_admin_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const COLORS = {
  primary: '#1B3A6B',
  primaryLight: '#2A4A7A',
  gold: '#C5A572',
  success: '#1D8F4E',
  warning: '#F59E0B',
  error: '#EF4444',
  white: '#FFFFFF',
  bg: '#F8FAFC',
  bgDark: '#F1F5F9',
  border: '#E2E8F0',
  text: '#1E293B',
  textMuted: '#64748B',
  textLight: '#94A3B8',
};

// Session state mapping from real conversation states
const STATE_CONFIG = {
  WELCOME: { label: 'ترحيب', color: COLORS.primary, icon: Clock },
  DISCOVERY: { label: 'استكشاف', color: COLORS.warning, icon: Play },
  REFLECTION: { label: 'انعكاس', color: COLORS.gold, icon: Pause },
  RESULT_READY: { label: 'نتائج جاهزة', color: COLORS.success, icon: CheckCircle },
  PLAN_READY: { label: 'خطة جاهزة', color: COLORS.success, icon: CheckCircle },
  SERVICE_ACTIVE: { label: 'خدمة نشطة', color: COLORS.success, icon: Play },
  SERVICE_IN_PROGRESS: { label: 'خدمة قيد التنفيذ', color: COLORS.warning, icon: Clock },
  SERVICE_SUBMITTED: { label: 'خدمة مكتملة', color: COLORS.success, icon: CheckCircle },
  CLIENT_INFO_CAPTURE: { label: 'جمع المعلومات', color: COLORS.warning, icon: User },
};

const NEEDS_MAP = {
  'sell_products': 'بيع منتجات',
  'develop_business': 'تطوير المشروع',
  'learn_skills': 'تعلم مهارات',
  'get_consultation': 'الحصول على استشارة',
  'find_workspace': 'إيجاد مساحة عمل',
  'funding': 'تمويل',
  'training': 'تدريب'
};

// Session Detail Drawer - Shows real conversation data
function SessionDrawer({ session, isOpen, onClose, onUpdate }) {
  const [activeTab, setActiveTab] = useState('conversation');
  const [reviewNotes, setReviewNotes] = useState('');
  
  if (!isOpen || !session) return null;
  
  const status = STATE_CONFIG[session.session_status] || STATE_CONFIG.WELCOME;
  const StatusIcon = status.icon;
  
  const handleAddReview = async () => {
    if (!reviewNotes.trim()) return;
    
    try {
      await axios.post(`${API}/sessions/${session.session_id}/review`, {
        reviewer_id: 'user_admin',
        reviewer_name: 'مدير النظام',
        status: 'reviewed',
        notes: reviewNotes
      }, { headers: getAuthHeaders() });
      setReviewNotes('');
      onUpdate();
    } catch (e) {
      console.error('Failed to add review:', e);
    }
  };
  
  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleString('ar-SA', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };
  
  return createPortal(
    <div style={{ position: 'fixed', inset: 0, zIndex: 9998 }}>
      {/* Overlay */}
      <div 
        style={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)' }}
        onClick={onClose}
      />
      
      {/* Drawer - Opens from the left side */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        bottom: 0,
        width: '600px',
        maxWidth: '90vw',
        backgroundColor: COLORS.white,
        boxShadow: '4px 0 30px rgba(0,0,0,0.2)',
        zIndex: 1,
        display: 'flex',
        flexDirection: 'column',
        overflowY: 'auto'
      }}>
        {/* Header */}
        <div style={{ 
          padding: '16px 20px', 
          borderBottom: `1px solid ${COLORS.border}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          backgroundColor: COLORS.primary,
          flexShrink: 0
        }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: COLORS.white }}>تفاصيل الجلسة</h2>
            <p style={{ margin: '4px 0 0', fontSize: '11px', color: 'rgba(255,255,255,0.7)', direction: 'ltr' }}>
              {session.session_id?.substring(0, 20)}...
            </p>
          </div>
          <button onClick={onClose} style={{
            width: '36px',
            height: '36px',
            borderRadius: '8px',
            border: 'none',
            backgroundColor: 'rgba(255,255,255,0.15)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <X size={18} color={COLORS.white} />
          </button>
        </div>
        
        {/* Session Info Cards */}
        <div style={{ padding: '20px 24px', borderBottom: `1px solid ${COLORS.border}`, backgroundColor: COLORS.bg }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
            <div style={{ 
              backgroundColor: COLORS.white, 
              padding: '14px', 
              borderRadius: '10px',
              border: `1px solid ${COLORS.border}`
            }}>
              <p style={{ fontSize: '11px', color: COLORS.textMuted, margin: '0 0 4px' }}>الحالة</p>
              <span style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                padding: '4px 10px',
                borderRadius: '6px',
                backgroundColor: `${status.color}15`,
                color: status.color,
                fontSize: '12px',
                fontWeight: 500
              }}>
                <StatusIcon size={12} />
                {status.label}
              </span>
            </div>
            <div style={{ 
              backgroundColor: COLORS.white, 
              padding: '14px', 
              borderRadius: '10px',
              border: `1px solid ${COLORS.border}`
            }}>
              <p style={{ fontSize: '11px', color: COLORS.textMuted, margin: '0 0 4px' }}>عدد الرسائل</p>
              <p style={{ fontSize: '20px', fontWeight: 700, color: COLORS.primary, margin: 0 }}>
                {session.message_count || 0}
              </p>
            </div>
            <div style={{ 
              backgroundColor: COLORS.white, 
              padding: '14px', 
              borderRadius: '10px',
              border: `1px solid ${COLORS.border}`
            }}>
              <p style={{ fontSize: '11px', color: COLORS.textMuted, margin: '0 0 4px' }}>تاريخ الإنشاء</p>
              <p style={{ fontSize: '12px', fontWeight: 500, color: COLORS.text, margin: 0 }}>
                {formatDate(session.created_at)}
              </p>
            </div>
          </div>
          
          {/* Client Info */}
          <div style={{ 
            marginTop: '16px',
            backgroundColor: COLORS.white, 
            padding: '16px', 
            borderRadius: '10px',
            border: `1px solid ${COLORS.border}`
          }}>
            <h3 style={{ fontSize: '13px', fontWeight: 600, color: COLORS.text, margin: '0 0 12px' }}>معلومات العميل</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div>
                <p style={{ fontSize: '11px', color: COLORS.textMuted, margin: '0 0 2px' }}>الاسم</p>
                <p style={{ fontSize: '14px', fontWeight: 500, color: COLORS.text, margin: 0 }}>
                  {session.client_name || 'غير محدد'}
                </p>
              </div>
              <div>
                <p style={{ fontSize: '11px', color: COLORS.textMuted, margin: '0 0 2px' }}>الجوال</p>
                <p style={{ fontSize: '14px', fontWeight: 500, color: COLORS.text, margin: 0, direction: 'ltr', textAlign: 'right' }}>
                  {session.mobile_number || '-'}
                </p>
              </div>
              <div>
                <p style={{ fontSize: '11px', color: COLORS.textMuted, margin: '0 0 2px' }}>البريد</p>
                <p style={{ fontSize: '14px', fontWeight: 500, color: COLORS.text, margin: 0 }}>
                  {session.email || '-'}
                </p>
              </div>
              <div>
                <p style={{ fontSize: '11px', color: COLORS.textMuted, margin: '0 0 2px' }}>نوع المستخدم</p>
                <p style={{ fontSize: '14px', fontWeight: 500, color: COLORS.text, margin: 0 }}>
                  {session.user_type === 'general' ? 'عام' : session.user_type || '-'}
                </p>
              </div>
              <div>
                <p style={{ fontSize: '11px', color: COLORS.textMuted, margin: '0 0 2px' }}>المشكلة الرئيسية</p>
                <p style={{ fontSize: '14px', fontWeight: 500, color: COLORS.text, margin: 0 }}>
                  {NEEDS_MAP[session.main_problem] || session.main_problem || '-'}
                </p>
              </div>
              <div>
                <p style={{ fontSize: '11px', color: COLORS.textMuted, margin: '0 0 2px' }}>نوع الدعم</p>
                <p style={{ fontSize: '14px', fontWeight: 500, color: COLORS.text, margin: 0 }}>
                  {session.support_type || '-'}
                </p>
              </div>
            </div>
          </div>
        </div>
        
        {/* Tabs */}
        <div style={{ display: 'flex', borderBottom: `1px solid ${COLORS.border}` }}>
          {['conversation', 'details'].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              style={{
                flex: 1,
                padding: '14px',
                border: 'none',
                backgroundColor: 'transparent',
                borderBottom: activeTab === tab ? `2px solid ${COLORS.primary}` : '2px solid transparent',
                color: activeTab === tab ? COLORS.primary : COLORS.textMuted,
                fontSize: '14px',
                fontWeight: activeTab === tab ? 600 : 400,
                cursor: 'pointer'
              }}
            >
              {tab === 'conversation' ? `المحادثة (${session.message_count || 0})` : 'تفاصيل إضافية'}
            </button>
          ))}
        </div>
        
        {/* Tab Content */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>
          {activeTab === 'conversation' ? (
            <div>
              {(!session.conversation || session.conversation.length === 0) ? (
                <div style={{ textAlign: 'center', padding: '60px 20px', color: COLORS.textMuted }}>
                  <MessageSquare size={40} style={{ marginBottom: '16px', opacity: 0.4 }} />
                  <p style={{ margin: 0, fontSize: '15px' }}>لا توجد رسائل في هذه الجلسة</p>
                  <p style={{ margin: '8px 0 0', fontSize: '13px', opacity: 0.7 }}>العميل لم يبدأ المحادثة بعد</p>
                </div>
              ) : (
                session.conversation.map((msg, i) => (
                  <div key={i} style={{
                    marginBottom: '16px',
                    display: 'flex',
                    flexDirection: msg.sender === 'user' ? 'row-reverse' : 'row',
                    gap: '10px'
                  }}>
                    <div style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '50%',
                      backgroundColor: msg.sender === 'user' ? COLORS.primary : COLORS.gold,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}>
                      {msg.sender === 'user' ? (
                        <User size={14} color={COLORS.white} />
                      ) : (
                        <span style={{ color: COLORS.primary, fontWeight: 'bold', fontSize: '12px' }}>هـ</span>
                      )}
                    </div>
                    <div style={{
                      maxWidth: '75%',
                      padding: '12px 16px',
                      borderRadius: msg.sender === 'user' ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                      backgroundColor: msg.sender === 'user' ? COLORS.primary : COLORS.bg,
                      color: msg.sender === 'user' ? COLORS.white : COLORS.text
                    }}>
                      <p style={{ margin: 0, fontSize: '14px', lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
                        {msg.message_text || msg.content}
                      </p>
                      <p style={{ 
                        margin: '8px 0 0', 
                        fontSize: '10px', 
                        opacity: 0.6 
                      }}>
                        {formatDate(msg.created_at || msg.timestamp)}
                      </p>
                    </div>
                  </div>
                ))
              )}
            </div>
          ) : (
            <div>
              {/* Discovery Data */}
              <div style={{ 
                backgroundColor: COLORS.bg, 
                padding: '16px', 
                borderRadius: '10px',
                marginBottom: '16px'
              }}>
                <h4 style={{ margin: '0 0 12px', fontSize: '14px', fontWeight: 600, color: COLORS.text }}>بيانات الاستكشاف</h4>
                <div style={{ display: 'grid', gap: '8px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: '13px', color: COLORS.textMuted }}>خطوة الاستكشاف</span>
                    <span style={{ fontSize: '13px', fontWeight: 500, color: COLORS.text }}>{session.discovery_step || 0}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: '13px', color: COLORS.textMuted }}>مرحلة المشروع</span>
                    <span style={{ fontSize: '13px', fontWeight: 500, color: COLORS.text }}>{session.project_stage || '-'}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: '13px', color: COLORS.textMuted }}>القطاع</span>
                    <span style={{ fontSize: '13px', fontWeight: 500, color: COLORS.text }}>{session.sector || '-'}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: '13px', color: COLORS.textMuted }}>تم جمع معلومات العميل</span>
                    <span style={{ fontSize: '13px', fontWeight: 500, color: session.client_info_collected ? COLORS.success : COLORS.textMuted }}>
                      {session.client_info_collected ? 'نعم' : 'لا'}
                    </span>
                  </div>
                </div>
              </div>
              
              {/* Add Review Note */}
              <div style={{ marginTop: '20px' }}>
                <h4 style={{ margin: '0 0 12px', fontSize: '14px', fontWeight: 600, color: COLORS.text }}>إضافة ملاحظة</h4>
                <textarea
                  value={reviewNotes}
                  onChange={e => setReviewNotes(e.target.value)}
                  placeholder="اكتب ملاحظتك هنا..."
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '8px',
                    border: `1px solid ${COLORS.border}`,
                    fontSize: '13px',
                    fontFamily: 'inherit',
                    resize: 'vertical',
                    minHeight: '80px',
                    outline: 'none'
                  }}
                />
                <button
                  onClick={handleAddReview}
                  disabled={!reviewNotes.trim()}
                  style={{
                    marginTop: '8px',
                    padding: '10px 20px',
                    borderRadius: '8px',
                    border: 'none',
                    backgroundColor: reviewNotes.trim() ? COLORS.primary : COLORS.bgDark,
                    color: reviewNotes.trim() ? COLORS.white : COLORS.textLight,
                    fontSize: '13px',
                    fontWeight: 500,
                    cursor: reviewNotes.trim() ? 'pointer' : 'not-allowed'
                  }}
                >
                  حفظ الملاحظة
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

export default function SessionsPage() {
  const [sessions, setSessions] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedSession, setSelectedSession] = useState(null);
  const [showDrawer, setShowDrawer] = useState(false);
  const [page, setPage] = useState(0);
  const limit = 20;

  useEffect(() => {
    fetchSessions();
  }, [searchQuery, statusFilter, page]);

  const fetchSessions = async () => {
    setLoading(true);
    try {
      let url = `${API}/sessions?skip=${page * limit}&limit=${limit}`;
      if (searchQuery) url += `&search=${encodeURIComponent(searchQuery)}`;
      if (statusFilter) url += `&status=${statusFilter}`;
      
      const res = await axios.get(url, { headers: getAuthHeaders() });
      setSessions(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch (e) {
      console.error('Failed to fetch sessions:', e);
      if (e.response?.status === 401) {
        window.location.href = '/admin/login';
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSessionClick = async (session) => {
    try {
      const res = await axios.get(`${API}/sessions/${session.session_id}`, { headers: getAuthHeaders() });
      setSelectedSession(res.data);
      setShowDrawer(true);
    } catch (e) {
      console.error('Failed to fetch session details:', e);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleString('ar-SA', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  const selectStyle = {
    padding: '10px 14px',
    borderRadius: '8px',
    border: `1px solid ${COLORS.border}`,
    backgroundColor: COLORS.white,
    fontSize: '13px',
    fontFamily: 'inherit',
    outline: 'none',
    cursor: 'pointer',
    minWidth: '160px'
  };

  return (
    <div>
      {/* Page Header */}
      <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h1 style={{ fontSize: '28px', fontWeight: 'bold', color: COLORS.text, margin: '0 0 8px' }}>الجلسات</h1>
          <p style={{ fontSize: '14px', color: COLORS.textMuted, margin: 0 }}>
            مراقبة جلسات المستخدمين الحقيقية من واجهة هلا • إجمالي {total} جلسة
          </p>
        </div>
        <button 
          onClick={() => fetchSessions()}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '10px 16px',
            borderRadius: '8px',
            border: `1px solid ${COLORS.border}`,
            backgroundColor: COLORS.white,
            fontSize: '13px',
            cursor: 'pointer'
          }}
        >
          <RefreshCw size={16} color={COLORS.textMuted} />
          تحديث
        </button>
      </div>

      {/* Stats Cards */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', 
        gap: '16px',
        marginBottom: '24px'
      }}>
        {Object.entries(STATE_CONFIG).slice(0, 5).map(([key, config]) => {
          const count = sessions.filter(s => s.session_status === key).length;
          const Icon = config.icon;
          return (
            <div 
              key={key}
              onClick={() => setStatusFilter(statusFilter === key ? '' : key)}
              style={{
                backgroundColor: statusFilter === key ? `${config.color}10` : COLORS.white,
                padding: '16px',
                borderRadius: '12px',
                border: `1px solid ${statusFilter === key ? config.color : COLORS.border}`,
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                <Icon size={16} color={config.color} />
                <span style={{ fontSize: '12px', color: COLORS.textMuted }}>{config.label}</span>
              </div>
              <p style={{ fontSize: '24px', fontWeight: 'bold', color: config.color, margin: 0 }}>{count}</p>
            </div>
          );
        })}
      </div>

      {/* Filters */}
      <div style={{
        backgroundColor: COLORS.white,
        borderRadius: '12px',
        padding: '16px 20px',
        marginBottom: '20px',
        border: `1px solid ${COLORS.border}`,
        display: 'flex',
        gap: '12px',
        flexWrap: 'wrap',
        alignItems: 'center'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          backgroundColor: COLORS.bg,
          padding: '10px 14px',
          borderRadius: '8px',
          flex: 1,
          minWidth: '280px'
        }}>
          <Search size={16} color={COLORS.textMuted} />
          <input
            type="text"
            placeholder="بحث بالاسم أو الجوال أو البريد أو رقم الجلسة..."
            value={searchQuery}
            onChange={e => { setSearchQuery(e.target.value); setPage(0); }}
            style={{
              border: 'none',
              backgroundColor: 'transparent',
              outline: 'none',
              flex: 1,
              fontSize: '13px',
              fontFamily: 'inherit'
            }}
          />
        </div>
        
        <select 
          value={statusFilter} 
          onChange={e => { setStatusFilter(e.target.value); setPage(0); }}
          style={selectStyle}
        >
          <option value="">كل الحالات</option>
          {Object.entries(STATE_CONFIG).map(([key, config]) => (
            <option key={key} value={key}>{config.label}</option>
          ))}
        </select>
        
        {(searchQuery || statusFilter) && (
          <button
            onClick={() => { setSearchQuery(''); setStatusFilter(''); setPage(0); }}
            style={{
              padding: '10px 14px',
              borderRadius: '8px',
              border: 'none',
              backgroundColor: COLORS.bgDark,
              fontSize: '13px',
              cursor: 'pointer',
              color: COLORS.textMuted
            }}
          >
            مسح الفلاتر
          </button>
        )}
      </div>

      {/* Sessions Table */}
      <div style={{
        backgroundColor: COLORS.white,
        borderRadius: '16px',
        border: `1px solid ${COLORS.border}`,
        overflow: 'hidden'
      }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: COLORS.bg }}>
                <th style={{ padding: '14px 16px', textAlign: 'right', fontSize: '12px', fontWeight: 600, color: COLORS.textMuted }}>العميل</th>
                <th style={{ padding: '14px 16px', textAlign: 'right', fontSize: '12px', fontWeight: 600, color: COLORS.textMuted }}>الحالة</th>
                <th style={{ padding: '14px 16px', textAlign: 'right', fontSize: '12px', fontWeight: 600, color: COLORS.textMuted }}>المشكلة</th>
                <th style={{ padding: '14px 16px', textAlign: 'right', fontSize: '12px', fontWeight: 600, color: COLORS.textMuted }}>الرسائل</th>
                <th style={{ padding: '14px 16px', textAlign: 'right', fontSize: '12px', fontWeight: 600, color: COLORS.textMuted }}>التاريخ</th>
                <th style={{ padding: '14px 16px', textAlign: 'right', fontSize: '12px', fontWeight: 600, color: COLORS.textMuted, width: '60px' }}></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="6" style={{ padding: '60px', textAlign: 'center', color: COLORS.textMuted }}>
                    <div style={{
                      width: '32px',
                      height: '32px',
                      border: `3px solid ${COLORS.border}`,
                      borderTopColor: COLORS.primary,
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite',
                      margin: '0 auto 12px'
                    }} />
                    جاري التحميل...
                    <style>{`@keyframes spin { to { transform: rotate(360deg); }}`}</style>
                  </td>
                </tr>
              ) : sessions.length === 0 ? (
                <tr>
                  <td colSpan="6" style={{ padding: '60px', textAlign: 'center', color: COLORS.textMuted }}>
                    <MessageSquare size={40} style={{ marginBottom: '12px', opacity: 0.4 }} />
                    <p style={{ margin: 0 }}>لا توجد جلسات</p>
                  </td>
                </tr>
              ) : (
                sessions.map(session => {
                  const status = STATE_CONFIG[session.session_status] || STATE_CONFIG.WELCOME;
                  const StatusIcon = status.icon;
                  
                  return (
                    <tr 
                      key={session.session_id} 
                      data-testid={`session-row-${session.session_id}`}
                      style={{ borderBottom: `1px solid ${COLORS.border}`, cursor: 'pointer', transition: 'background 0.2s' }}
                      onClick={() => handleSessionClick(session)}
                      onMouseEnter={e => e.currentTarget.style.backgroundColor = COLORS.bg}
                      onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                      <td style={{ padding: '16px' }}>
                        <div>
                          <p style={{ margin: 0, fontSize: '14px', fontWeight: 500, color: COLORS.text }}>
                            {session.client_name || 'مستخدم جديد'}
                          </p>
                          <p style={{ margin: '2px 0 0', fontSize: '12px', color: COLORS.textMuted }}>
                            {session.email || session.mobile_number || session.session_id?.substring(0, 12) + '...'}
                          </p>
                        </div>
                      </td>
                      <td style={{ padding: '16px' }}>
                        <span style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: '4px 10px',
                          borderRadius: '6px',
                          backgroundColor: `${status.color}15`,
                          color: status.color,
                          fontSize: '12px',
                          fontWeight: 500
                        }}>
                          <StatusIcon size={12} />
                          {status.label}
                        </span>
                      </td>
                      <td style={{ padding: '16px' }}>
                        <span style={{ fontSize: '13px', color: COLORS.textMuted }}>
                          {NEEDS_MAP[session.main_problem] || session.main_problem || '-'}
                        </span>
                      </td>
                      <td style={{ padding: '16px' }}>
                        <span style={{ 
                          fontSize: '14px', 
                          fontWeight: 600, 
                          color: session.message_count > 0 ? COLORS.primary : COLORS.textLight 
                        }}>
                          {session.message_count || 0}
                        </span>
                      </td>
                      <td style={{ padding: '16px' }}>
                        <span style={{ fontSize: '12px', color: COLORS.textLight }}>
                          {formatDate(session.created_at)}
                        </span>
                      </td>
                      <td style={{ padding: '16px' }}>
                        <button style={{
                          width: '32px',
                          height: '32px',
                          borderRadius: '6px',
                          border: 'none',
                          backgroundColor: COLORS.bg,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <Eye size={16} color={COLORS.textMuted} />
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        
        {/* Pagination */}
        {total > limit && (
          <div style={{ 
            padding: '16px 20px', 
            borderTop: `1px solid ${COLORS.border}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <span style={{ fontSize: '13px', color: COLORS.textMuted }}>
              عرض {page * limit + 1} - {Math.min((page + 1) * limit, total)} من {total}
            </span>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={() => setPage(p => Math.max(0, p - 1))}
                disabled={page === 0}
                style={{
                  padding: '8px 14px',
                  borderRadius: '6px',
                  border: `1px solid ${COLORS.border}`,
                  backgroundColor: COLORS.white,
                  fontSize: '13px',
                  cursor: page === 0 ? 'not-allowed' : 'pointer',
                  opacity: page === 0 ? 0.5 : 1
                }}
              >
                السابق
              </button>
              <button
                onClick={() => setPage(p => p + 1)}
                disabled={(page + 1) * limit >= total}
                style={{
                  padding: '8px 14px',
                  borderRadius: '6px',
                  border: `1px solid ${COLORS.border}`,
                  backgroundColor: COLORS.white,
                  fontSize: '13px',
                  cursor: (page + 1) * limit >= total ? 'not-allowed' : 'pointer',
                  opacity: (page + 1) * limit >= total ? 0.5 : 1
                }}
              >
                التالي
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Session Drawer */}
      <SessionDrawer
        session={selectedSession}
        isOpen={showDrawer}
        onClose={() => { setShowDrawer(false); setSelectedSession(null); }}
        onUpdate={() => { fetchSessions(); if (selectedSession) handleSessionClick(selectedSession); }}
      />
    </div>
  );
}
